import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, localDay, money } from '../lib/format.ts';
import { device, useSession } from '../lib/session.tsx';
import type { Register } from '../lib/types.ts';
import { useUi } from '../lib/ui-state.tsx';
import { CustomerPicker, type CustomerCard } from './Customers.tsx';
import { Icon } from './Icon.tsx';
import { Button, Dialog, Field, IconButton, Keypad } from './ui.tsx';

export interface GiftCardView {
  id: string;
  /** Only for people who sell cards; others see the last four characters. */
  code: string | null;
  tail: string;
  initial: number;
  balance: number;
  state: 'active' | 'void';
  expires_on: string;
  expired: boolean;
  usable: boolean;
  problem: string | null;
  customer: string | null;
  customer_id: string | null;
  note: string | null;
  void_reason: string | null;
  created_at: string;
}

export interface CustomerPoints {
  enabled: boolean;
  points: number;
  value: number;
  point_value: number;
  earn_per: number;
  min_redeem: number;
}

/** One way to write a code however it was typed, as the server does. */
export function normalizeCode(raw: string): string {
  const c = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return c.length === 12 ? `${c.slice(0, 4)}-${c.slice(4, 8)}-${c.slice(8)}` : c;
}

/** "ABCD-EFGH-JKMN" shown as "•••• •••• JKMN" where the full code must not be seen. */
export const maskedCode = (c: { code: string | null; tail: string }) => c.code ?? `•••• •••• ${c.tail}`;

/** What is in the wallet of the customer on a bill (0 when none, or nothing to see). */
export function useCustomerWallet(customer: string | null | undefined) {
  const { can } = useSession();
  const [balance, setBalance] = useState(0);
  useEffect(() => {
    setBalance(0);
    if (!customer || !can('loyalty.view')) return;
    action<{ balance: number }>('loyalty.wallet', { customer }).then((w) => setBalance(w.balance), () => setBalance(0));
  }, [customer, can]);
  return balance;
}

/** A customer's points, when the loyalty module is there and the programme is on. */
export function useCustomerPoints(customer: string | null | undefined) {
  const { can } = useSession();
  const [data, setData] = useState<CustomerPoints | null>(null);
  useEffect(() => {
    setData(null);
    if (!customer || !can('loyalty.view')) return;
    // Points are a convenience on the pay screen: a failure here never blocks paying.
    action<CustomerPoints>('loyalty.customer', { customer }).then(setData, () => setData(null));
  }, [customer, can]);
  return data?.enabled ? data : null;
}

function keyed(current: string, k: string, max = 9): string {
  if (k === 'back') return current.slice(0, -1);
  if (k === '00') return current ? `${current}00`.slice(0, max) : current;
  return `${current}${k}`.replace(/^0+/, '').slice(0, max);
}

/** Pay with a gift card: find it by its code, see what is on it, take part or all of the bill. */
export function GiftCardPay({
  remaining,
  used,
  busy,
  onTake,
}: {
  remaining: number;
  /** Already taken from each card on this bill, by code. */
  used: Record<string, number>;
  busy: boolean;
  onTake: (code: string, amount: number) => void;
}) {
  const { fail } = useUi();
  const [code, setCode] = useState('');
  const [card, setCard] = useState<{ code: string; view: GiftCardView } | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typed, setTyped] = useState('');

  const check = async () => {
    const c = normalizeCode(code);
    if (c.replace(/-/g, '').length < 4) {
      setError(t('gift.code_short'));
      return;
    }
    setChecking(true);
    setError(null);
    try {
      setCard({ code: c, view: await action<GiftCardView>('loyalty.lookup_card', { code: c }) });
      setTyped('');
    } catch (err) {
      setCard(null);
      if ((err as { code?: string }).code === 'not_found') setError(t('gift.not_found'));
      else fail(err);
    } finally {
      setChecking(false);
    }
  };

  const available = card ? Math.max(0, card.view.balance - (used[card.code] ?? 0)) : 0;
  const entered = typed ? Number(typed) * 100 : null;
  const amount = entered ?? Math.min(available, remaining);
  const tooMuch = entered !== null && (entered > available || entered > remaining);

  return (
    <div className="gift-pay">
      <form
        className="gift-find"
        onSubmit={(e) => {
          e.preventDefault();
          void check();
        }}
      >
        <Field label={t('gift.code')} error={error}>
          <input
            className="input mono"
            value={code}
            placeholder="XXXX-XXXX-XXXX"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={20}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setError(null);
              if (card) setCard(null);
            }}
            data-autofocus
          />
        </Field>
        <Button type="submit" icon="search" busy={checking} disabled={!code.trim()}>
          {t('gift.check')}
        </Button>
      </form>

      {card ? (
        card.view.usable && available > 0 ? (
          <>
            <div className="gift-found" role="status">
              <span className="gift-badge">
                <Icon name="gift" size={22} />
              </span>
              <span className="gift-found-main">
                <strong>{t('gift.card_tail', { tail: card.view.tail })}</strong>
                <span className="muted">
                  {t('gift.valid_until', { date: day(card.view.expires_on) })}
                  {card.view.customer ? ` · ${card.view.customer}` : ''}
                </span>
              </span>
              <span className="gift-found-balance">
                <span className="muted">{used[card.code] ? t('gift.left_now') : t('gift.balance')}</span>
                <strong>{money(available)}</strong>
              </span>
            </div>
            <div className="amount-display">
              <span className="muted">{t('pay.amount')}</span>
              <strong className={tooMuch ? 'text-danger' : undefined}>{money(amount)}</strong>
              {typed ? <IconButton icon="x" label={t('common.clear')} onClick={() => setTyped('')} /> : null}
            </div>
            {tooMuch ? (
              <p className="field-msg" role="alert">
                <Icon name="alert" size={14} /> {entered! > available ? t('gift.more_than_card') : t('gift.more_than_bill')}
              </p>
            ) : null}
            <Keypad onKey={(k) => setTyped((x) => keyed(x, k))} extra={{ key: '00', label: '00' }} />
            <Button
              variant="success"
              size="xl"
              icon="gift"
              className="w-full"
              busy={busy}
              disabled={amount <= 0 || tooMuch}
              onClick={() => {
                onTake(card.code, amount);
                setTyped('');
              }}
            >
              {t('gift.take', { amount: money(amount) })}
            </Button>
          </>
        ) : (
          <div className="gift-found problem" role="alert">
            <span className="gift-badge">
              <Icon name="alert" size={22} />
            </span>
            <span className="gift-found-main">
              <strong>{t('gift.card_tail', { tail: card.view.tail })}</strong>
              <span>
                {card.view.state === 'void'
                  ? t('gift.problem_void')
                  : card.view.expired
                    ? t('gift.problem_expired', { date: day(card.view.expires_on) })
                    : used[card.code]
                      ? t('gift.problem_all_used')
                      : t('gift.problem_empty')}
              </span>
            </span>
          </div>
        )
      ) : (
        <p className="muted gift-hint">
          <Icon name="gift" size={16} /> {t('gift.hint')}
        </p>
      )}
    </div>
  );
}

/** Pay with the points of the customer on the bill. */
export function PointsPay({
  points,
  remaining,
  usedPoints,
  busy,
  onTake,
}: {
  points: CustomerPoints;
  remaining: number;
  /** Points already put on this bill. */
  usedPoints: number;
  busy: boolean;
  onTake: (points: number, amount: number) => void;
}) {
  const [typed, setTyped] = useState('');
  const have = points.points - usedPoints;
  const max = Math.max(0, Math.min(have, Math.floor(remaining / points.point_value)));
  const entered = typed ? Number(typed) : null;
  const n = entered ?? max;
  const problem =
    have < points.min_redeem
      ? t('points.not_enough', { min: points.min_redeem })
      : max < points.min_redeem
        ? t('points.bill_too_small', { min: points.min_redeem })
        : n > have
          ? tn('points.only_have', have)
          : n > max
            ? t('points.more_than_bill', { max })
            : n < points.min_redeem
              ? t('points.at_least', { min: points.min_redeem })
              : null;
  const quick = [...new Set([points.min_redeem, Math.floor(max / 2), max])].filter((v) => v >= points.min_redeem && v <= max).sort((a, b) => a - b);

  return (
    <div className="points-pay">
      <div className="points-have">
        <span className="gift-badge points">
          <Icon name="star" size={22} />
        </span>
        <span className="gift-found-main">
          <strong>{tn('points.count', Math.max(0, have))}</strong>
          <span className="muted">{t('points.worth', { amount: money(Math.max(0, have) * points.point_value), each: money(points.point_value) })}</span>
        </span>
      </div>
      {quick.length ? (
        <div className="quick-cash">
          {quick.map((v) => (
            <Button key={v} size="lg" variant={entered === v || (entered === null && v === max) ? 'success' : 'secondary'} onClick={() => setTyped(String(v))}>
              {tn('points.count', v)}
            </Button>
          ))}
        </div>
      ) : null}
      <div className="amount-display">
        <span className="muted">{t('points.to_use')}</span>
        <strong>{tn('points.count', n)}</strong>
        <span className="muted">{money(n * points.point_value)}</span>
      </div>
      {problem ? (
        <p className="field-msg" role="alert">
          <Icon name="alert" size={14} /> {problem}
        </p>
      ) : null}
      <Keypad onKey={(k) => setTyped((x) => keyed(x, k, 7))} />
      <Button
        variant="success"
        size="xl"
        icon="star"
        className="w-full"
        busy={busy}
        disabled={!!problem || n <= 0}
        onClick={() => {
          onTake(n, n * points.point_value);
          setTyped('');
        }}
      >
        {t('points.use', { n, amount: money(n * points.point_value) })}
      </Button>
    </div>
  );
}

interface SoldCard extends GiftCardView {
  code: string;
}

const GIFT_AMOUNTS = [1_000_00, 2_000_00, 5_000_00, 10_000_00];

/** Sells a gift card at the till: the amount, how it is paid and, if wanted, whose it is. */
export function SellGiftCardDialog({ session, onClose, onSold }: { session: string; onClose: () => void; onSold: () => void }) {
  const { fail, toast } = useUi();
  const [typed, setTyped] = useState('');
  const [method, setMethod] = useState<'cash' | 'card'>('cash');
  const [customer, setCustomer] = useState<CustomerCard | null>(null);
  const [picking, setPicking] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [sold, setSold] = useState<SoldCard | null>(null);
  const amount = typed ? Number(typed) * 100 : 0;

  const sell = async () => {
    setBusy(true);
    try {
      const card = await action<SoldCard>('loyalty.sell_card', {
        session,
        amount,
        method,
        customer: customer?.id ?? null,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setSold(card);
      toast(t('gift.sold_toast', { amount: money(card.initial) }));
      onSold();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  if (sold) return <GiftCardSold card={sold} onClose={onClose} />;

  return (
    <Dialog
      title={t('gift.sell_title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="gift" busy={busy} disabled={amount < 100} onClick={() => void sell()}>
            {amount ? t('gift.sell_for', { amount: money(amount) }) : t('gift.choose_amount')}
          </Button>
        </>
      }
    >
      <div className="gift-sell">
        <div className="quick-cash four">
          {GIFT_AMOUNTS.map((v) => (
            <Button key={v} size="lg" variant={amount === v ? 'success' : 'secondary'} aria-pressed={amount === v} onClick={() => setTyped(String(v / 100))}>
              {money(v)}
            </Button>
          ))}
        </div>
        <div className="amount-display">
          <span className="muted">{t('gift.value')}</span>
          <strong>{amount ? money(amount) : '–'}</strong>
          {typed ? <IconButton icon="x" label={t('common.clear')} onClick={() => setTyped('')} /> : null}
        </div>
        <Keypad onKey={(k) => setTyped((x) => keyed(x, k, 7))} extra={{ key: '00', label: '00' }} />
        <div className="field">
          <span className="field-label" id="gift-paid-by">
            {t('gift.paid_by')}
          </span>
          <div className="segmented" role="radiogroup" aria-labelledby="gift-paid-by">
            {(['cash', 'card'] as const).map((m) => (
              <button key={m} role="radio" aria-checked={method === m} className={method === m ? 'active' : ''} onClick={() => setMethod(m)}>
                <Icon name={m} size={18} /> {t(`pay.method_${m}`)}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <span className="field-label">{t('gift.for_customer')}</span>
          {customer ? (
            <div className="gift-customer">
              <Icon name="users" size={18} />
              <strong>{customer.name}</strong>
              <IconButton icon="x" label={t('gift.no_customer')} onClick={() => setCustomer(null)} />
            </div>
          ) : (
            <Button icon="plus" variant="secondary" onClick={() => setPicking(true)}>
              {t('gift.add_customer')}
            </Button>
          )}
          <span className="field-hint">{t('gift.customer_hint')}</span>
        </div>
        <Field label={t('gift.note')} hint={t('gift.note_hint')}>
          <input className="input" value={note} maxLength={120} onChange={(e) => setNote(e.target.value)} autoComplete="off" />
        </Field>
      </div>
      {picking ? (
        <CustomerPicker
          title={t('gift.for_customer')}
          onPick={(c) => {
            setCustomer(c);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </Dialog>
  );
}

/** The card just sold: its code, big enough to read out or write on a card, and printable. */
export function GiftCardSold({ card, onClose }: { card: SoldCard; onClose: () => void }) {
  const { tenant } = useSession();
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    if (!printing) return;
    // Printed on the receipt paper, like a receipt: the sheet exists only while printing.
    const id = requestAnimationFrame(() => {
      window.print();
      setPrinting(false);
    });
    return () => cancelAnimationFrame(id);
  }, [printing]);
  const slip = (
    <article className="gift-slip" aria-label={t('gift.slip')}>
      {tenant?.name ? <span className="gift-slip-brand">{tenant.name}</span> : null}
      <span className="gift-slip-title">
        <Icon name="gift" size={20} /> {t('gift.slip_title')}
      </span>
      <strong className="gift-slip-amount">{money(card.initial)}</strong>
      <span className="gift-slip-code mono" aria-label={t('gift.code')}>
        {card.code}
      </span>
      <span className="gift-slip-meta">{t('gift.valid_until', { date: day(card.expires_on) })}</span>
      {card.customer ? <span className="gift-slip-meta">{card.customer}</span> : null}
    </article>
  );
  return (
    <Dialog title={t('gift.sold_title')} onClose={onClose} size="sm">
      <div className="gift-sold">
        {slip}
        <p className="muted">{t('gift.sold_hint')}</p>
        <Button size="lg" icon="printer" className="w-full" onClick={() => setPrinting(true)}>
          {t('gift.print')}
        </Button>
        <Button variant="primary" size="lg" className="w-full" onClick={onClose} data-autofocus>
          {t('common.close')}
        </Button>
      </div>
      {printing
        ? createPortal(
            <div className="print-sheet">
              <div className="paper gift-paper">{slip}</div>
            </div>,
            document.body,
          )
        : null}
    </Dialog>
  );
}

export interface ProfileLoyalty {
  enabled: boolean;
  points: number;
  value: number;
  history: { id: string; kind: 'earn' | 'redeem' | 'back' | 'reverse' | 'adjust'; points: number; amount: number; note: string | null; created_at: string; receipt_no: number | null }[];
  gift_cards: { id: string; tail: string; balance: number; state: 'active' | 'void'; expires_on: string; expired: boolean }[];
  /** What is in the customer's wallet; null when they have none yet. */
  wallet?: number | null;
}

const KIND_LABEL = {
  earn: 'points.kind_earn',
  redeem: 'points.kind_redeem',
  back: 'points.kind_back',
  reverse: 'points.kind_reverse',
  adjust: 'points.kind_adjust',
} as const;

/** Points and gift cards on a customer's profile. */
export function CustomerLoyalty({ customer, data, onChanged }: { customer: string; data: ProfileLoyalty; onChanged: () => void }) {
  const { can } = useSession();
  const [adjusting, setAdjusting] = useState(false);
  const [topping, setTopping] = useState(false);
  const canTopUp = can('loyalty.sell') && can('pos.pay');
  const hasWallet = data.wallet !== null && data.wallet !== undefined;
  if (!data.enabled && !data.points && !data.gift_cards.length && !hasWallet && !canTopUp) return null;
  return (
    <section className="profile-loyalty" aria-labelledby="profile-loyalty-title">
      <div className="profile-loyalty-head">
        <h3 id="profile-loyalty-title">{hasWallet || canTopUp ? t('points.title_wallet') : t('points.title')}</h3>
        {can('loyalty.manage') ? (
          <Button size="sm" variant="ghost" icon="edit" onClick={() => setAdjusting(true)}>
            {t('points.adjust')}
          </Button>
        ) : null}
      </div>
      <div className="loyalty-cards">
        {hasWallet || canTopUp ? (
          <div className="loyalty-tile wallet-tile">
            <span className="gift-badge wallet">
              <Icon name="wallet" size={20} />
            </span>
            <span className="gift-found-main">
              <strong>{money(data.wallet ?? 0)}</strong>
              <span className="muted">{hasWallet ? t('wallet.in_wallet') : t('wallet.none')}</span>
            </span>
            {canTopUp ? (
              <Button size="sm" variant="secondary" icon="plus" onClick={() => setTopping(true)}>
                {t('wallet.top_up')}
              </Button>
            ) : null}
          </div>
        ) : null}
        <div className="loyalty-tile">
          <span className="gift-badge points">
            <Icon name="star" size={20} />
          </span>
          <span className="gift-found-main">
            <strong>{tn('points.count', data.points)}</strong>
            <span className="muted">{data.enabled ? t('points.worth_short', { amount: money(data.value) }) : t('points.programme_off')}</span>
          </span>
        </div>
        {data.gift_cards.map((g) => (
          <div key={g.id} className={`loyalty-tile${g.state === 'void' || g.expired || !g.balance ? ' spent' : ''}`}>
            <span className="gift-badge">
              <Icon name="gift" size={20} />
            </span>
            <span className="gift-found-main">
              <strong>{money(g.balance)}</strong>
              <span className="muted">
                {t('gift.card_tail', { tail: g.tail })} ·{' '}
                {g.state === 'void' ? t('gift.state_void') : g.expired ? t('gift.state_expired') : t('gift.valid_until', { date: day(g.expires_on) })}
              </span>
            </span>
          </div>
        ))}
      </div>
      {data.history.length ? (
        <ul className="rep-reasons">
          {data.history.slice(0, 6).map((h) => (
            <li key={h.id}>
              <span>
                {localDay(h.created_at)} · {t(KIND_LABEL[h.kind])}
                {h.receipt_no ? ` · ${t('points.on_receipt', { n: h.receipt_no })}` : ''}
                {h.note ? <span className="muted"> · {h.note}</span> : null}
              </span>
              <span className={`strong ${h.points > 0 ? 'text-ok' : ''}`}>{h.points > 0 ? `+${h.points}` : h.points}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {topping ? (
        <TopUpWalletDialog
          customer={customer}
          onClose={() => setTopping(false)}
          onDone={() => {
            setTopping(false);
            onChanged();
          }}
        />
      ) : null}
      {adjusting ? (
        <AdjustPointsDialog
          customer={customer}
          balance={data.points}
          onClose={() => setAdjusting(false)}
          onDone={() => {
            setAdjusting(false);
            onChanged();
          }}
        />
      ) : null}
    </section>
  );
}

function AdjustPointsDialog({ customer, balance, onClose, onDone }: { customer: string; balance: number; onClose: () => void; onDone: () => void }) {
  const { fail, toast } = useUi();
  const [sign, setSign] = useState<1 | -1>(1);
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const n = Number(typed || 0) * sign;
  const tooMany = balance + n < 0;
  const save = async () => {
    setBusy(true);
    try {
      const r = await action<{ points: number }>('loyalty.adjust_points', { customer, points: n, reason: reason.trim() });
      toast(tn('points.adjusted', r.points));
      onDone();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('points.adjust')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!n || tooMany || reason.trim().length < 3} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="gift-sell">
        <div className="segmented" role="radiogroup" aria-label={t('points.adjust')}>
          {([1, -1] as const).map((s) => (
            <button key={s} role="radio" aria-checked={sign === s} className={sign === s ? 'active' : ''} onClick={() => setSign(s)}>
              <Icon name={s > 0 ? 'plus' : 'minus'} size={18} /> {s > 0 ? t('points.add') : t('points.take_off')}
            </button>
          ))}
        </div>
        <div className="amount-display">
          <span className="muted">{t('points.after', { n: balance + n })}</span>
          <strong className={tooMany ? 'text-danger' : undefined}>{tn('points.count', Math.abs(n))}</strong>
        </div>
        <Keypad onKey={(k) => setTyped((x) => keyed(x, k, 6))} />
        <Field label={t('points.reason')} hint={t('points.reason_hint')}>
          <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} autoComplete="off" />
        </Field>
      </div>
    </Dialog>
  );
}

/**
 * Money a customer puts in their wallet at the till, to spend later on any bill with their name:
 * taken in cash or by card on this screen's register, like a gift card sold.
 */
function TopUpWalletDialog({ customer, onClose, onDone }: { customer: string; onClose: () => void; onDone: () => void }) {
  const { fail, toast } = useUi();
  const [registers, setRegisters] = useState<Register[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(device.register);
  const [typed, setTyped] = useState('');
  const [method, setMethod] = useState<'cash' | 'card'>('cash');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    action<Register[]>('pos.registers', {}).then(setRegisters, fail);
  }, [fail]);
  const open = (registers ?? []).filter((r) => r.session);
  const register = open.find((r) => r.id === chosen) ?? (open.length === 1 ? open[0] : undefined);
  const amount = typed ? Number(typed) * 100 : 0;
  const save = async () => {
    if (!register?.session) return;
    setBusy(true);
    try {
      const r = await action<{ balance: number }>('loyalty.top_up_wallet', { customer, session: register.session.id, amount, method });
      toast(t('wallet.topped_up', { amount: money(amount), balance: money(r.balance) }));
      onDone();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('wallet.top_up_title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="wallet" busy={busy} disabled={amount < 100 || !register} onClick={() => void save()}>
            {amount ? t('wallet.top_up_for', { amount: money(amount) }) : t('gift.choose_amount')}
          </Button>
        </>
      }
    >
      {registers && !open.length ? (
        <p className="callout callout-warn">
          <Icon name="register" size={16} /> {t('wallet.no_shift')}
        </p>
      ) : null}
      {open.length > 1 ? (
        <div className="field">
          <span className="field-label" id="wallet-register">
            {t('wallet.register')}
          </span>
          <div className="segmented" role="radiogroup" aria-labelledby="wallet-register">
            {open.map((r) => (
              <button
                key={r.id}
                role="radio"
                aria-checked={register?.id === r.id}
                className={register?.id === r.id ? 'active' : ''}
                onClick={() => {
                  device.register = r.id;
                  setChosen(r.id);
                }}
              >
                <Icon name="register" size={18} /> {r.name}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <div className="gift-sell">
        <div className="quick-cash four">
          {GIFT_AMOUNTS.map((v) => (
            <Button key={v} size="lg" variant={amount === v ? 'success' : 'secondary'} aria-pressed={amount === v} onClick={() => setTyped(String(v / 100))}>
              {money(v)}
            </Button>
          ))}
        </div>
        <div className="amount-display">
          <span className="muted">{t('wallet.amount')}</span>
          <strong>{amount ? money(amount) : '–'}</strong>
          {typed ? <IconButton icon="x" label={t('common.clear')} onClick={() => setTyped('')} /> : null}
        </div>
        <Keypad onKey={(k) => setTyped((x) => keyed(x, k, 7))} extra={{ key: '00', label: '00' }} />
        <div className="field">
          <span className="field-label" id="wallet-paid-by">
            {t('gift.paid_by')}
          </span>
          <div className="segmented" role="radiogroup" aria-labelledby="wallet-paid-by">
            {(['cash', 'card'] as const).map((m) => (
              <button key={m} role="radio" aria-checked={method === m} className={method === m ? 'active' : ''} onClick={() => setMethod(m)}>
                <Icon name={m} size={18} /> {t(`pay.method_${m}`)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
